'use client';

import {
  TextField as AUITextField,
  TextFieldProps as AUITextFieldProps,
  TextFieldStatus,
} from '@/lib/mx-asurion-ui-react';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';
import {
  isValidEmail,
  isValidFirstName,
  isValidLastName,
} from '@/utils/validations';
import { ChangeEvent, FormEvent, useState } from 'react';

type ErrorTypes =
  | 'blankInputError'
  | 'invalidInputError'
  | 'incompleteNameError';

export type ErrorHelperTexts = { [K in ErrorTypes | string]?: string };

type FormTextFieldProps = Omit<
  AUITextFieldProps,
  'helperText' | 'fieldStatus' | 'value'
> & {
  defaultHelperText?: string;
  errorHelperText?: string;
  value?: string;
  errorTexts?: ErrorHelperTexts;
};

type ValidationConfig = {
  validate: (value: string) => boolean;
  format?: (input: string) => string;
};

function getValidationConfig(type: string): ValidationConfig {
  switch (type) {
    case 'name':
      return {
        validate: (value) => isValidFirstName(value) || isValidLastName(value),
      };
    case 'email':
      return {
        validate: (value) => isValidEmail(value),
      };
    case 'tel':
      return {
        validate: (value) => value.length === 14,
        format: formatPhoneNumber,
      };
    case 'num':
      return {
        validate: (value) => /^\d+$/.test(value),
      };
    case 'streetAddress':
      return {
        validate: (value) => {
          const [first, ...rest] = value.split(' ');
          return (
            !isNaN(Number(first)) &&
            rest.length > 0 &&
            rest.every((word) => word.length >= 1)
          );
        },
      };
    default:
      return {
        validate: () => true,
      };
  }
}

export default function FormTextField({
  type = 'text',
  defaultHelperText = '',
  errorHelperText = '',
  errorTexts,
  required,
  ...props
}: FormTextFieldProps) {
  const [fieldStatus, setFieldStatus] = useState<TextFieldStatus>('default');
  const [controlledValue, setControlledValue] = useState<string>(
    props.value || '',
  );
  const [errorType, setErrorType] = useState<ErrorTypes | null>(null);
  const { validate, format = (input: string) => input } =
    getValidationConfig(type);

  // this is needed so that when the form submits without the user interacting
  // with a field, if it is marked as required, it will display the
  // error message appropriately
  const onInvalid = () => {
    setFieldStatus('error');
  };

  const verifyLength = (value: string): boolean => {
    if (props.minLength) {
      return value.length >= props.minLength;
    }

    if (props.maxLength) {
      return value.length <= props.maxLength;
    }
    return true;
  };

  const onBlur = (e: FormEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    const validValue = validate(target.value);
    const validMinLength = verifyLength(target.value);

    if (target.value.length === 0) {
      setErrorType('blankInputError');
    } else if (!validValue) {
      setErrorType('invalidInputError');
    } else if (!validMinLength) {
      setErrorType('incompleteNameError');
    }

    if (
      target.value.length === 0
        ? !required
        : verifyLength(target.value) && validate(target.value)
    ) {
      setFieldStatus('default');
      target.setCustomValidity('');
    } else {
      setFieldStatus('error');
      target.setCustomValidity('invalid');
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    props.onChange?.(e);
    setControlledValue(format(target.value));
  };

  const shouldMaskRecording = ['tel', 'name', 'email'].includes(type);
  return (
    <AUITextField
      inputClassName={shouldMaskRecording ? 'fs-mask' : undefined}
      type={type}
      onBlur={onBlur}
      onChange={onChange}
      onInvalid={onInvalid}
      helperText={
        fieldStatus === 'default'
          ? defaultHelperText
          : errorTexts && errorType
            ? errorTexts[errorType]
            : errorHelperText
      }
      fieldStatus={fieldStatus}
      value={controlledValue}
      required={required}
      {...props}
    />
  );
}
